import { isUserAuthorized } from '@src/hooks/useRoleAuth';
import {
  ACTION,
  isValidQuotePrice,
  QuoteStatusEnum,
  SideEnum,
  type Quote,
  type QuoteMarket,
  type User,
} from '@talos/kyoko';
import { calculateBestQuotes, type QuoteMarketWithBestBidOffer, type RfqBestQuoteBy } from '../../../utils/rfq';

/** Sell actions consume the market bid; buy actions consume the market offer. */
export function getQuoteTradeSidePrice(
  quoteMarket: Pick<QuoteMarket, 'BidPx' | 'OfferPx'> | undefined,
  side: SideEnum
): string | undefined {
  return side === SideEnum.Sell ? quoteMarket?.BidPx : quoteMarket?.OfferPx;
}

/**
 * Resolves whether a Quotes V2 trading action can trade a specific side.
 *
 * The action is disabled unless the quote and quote market are open, the relevant side has a price, the current
 * user can accept RFQs, the quote belongs to that user, and any parent care-order side matches the requested side.
 */
export function isQuoteTradeSideDisabled({
  quote,
  quoteMarket,
  side,
  user,
  careOrderSide,
}: {
  quote: Pick<Quote, 'PricingMode' | 'QuoteStatus' | 'User'>;
  quoteMarket: Pick<QuoteMarket, 'BidPx' | 'OfferPx' | 'QuoteStatus'> | undefined;
  side: SideEnum;
  user: User;
  careOrderSide?: SideEnum;
}): boolean {
  const price = getQuoteTradeSidePrice(quoteMarket, side);
  const hasPrice =
    price != null && price !== '' && isValidQuotePrice(price, quoteMarket?.QuoteStatus, quote.PricingMode);
  const canAccept = isUserAuthorized(user, ACTION.ACCEPT_RFQ);
  const isOwnQuote = quote.User === user.Name;
  const sideRestricted = !isOwnQuote || (careOrderSide != null && careOrderSide !== side);

  return (
    quote.QuoteStatus !== QuoteStatusEnum.Open ||
    quoteMarket?.QuoteStatus !== QuoteStatusEnum.Open ||
    !hasPrice ||
    !canAccept ||
    sideRestricted
  );
}

/**
 * Looks into Quote.Markets and grabs the relevant markets to elevate into the QuoteRow for the QuotesBlotter
 *
 * If filled / pending fix, will grab the bid-offer for whatever market we traded on (even if it wasn't the best price). Will
 * still flag the traded market with isBestBid, isBestOffer correctly despite it being a slightly different codepath
 *
 * Otherwise, will elevate the best open bid-offers (which can be on separate markets)
 *
 */
export function getQuoteMarketsForQuoteRow(
  quote: Quote,
  bestBy?: RfqBestQuoteBy
): {
  bid: QuoteMarketWithBestBidOffer | undefined;
  offer: QuoteMarketWithBestBidOffer | undefined;
} {
  // If the quote is filled (we have a traded market), then we always surface the traded market's pricing details into the quote row
  if (quote.tradedMarket) {
    // False here means that we calculate prices for all markets, not just open markets
    const bests = calculateBestQuotes({
      quotes: quote.Markets,
      pricingMode: quote.PricingMode,
      onlyActive: false,
      bestBy,
    });
    const isBestBid = bests.find(m => m.isBestBid)?.Market === quote.tradedMarket.Market;
    const isBestOffer = bests.find(m => m.isBestOffer)?.Market === quote.tradedMarket.Market;

    return {
      bid: { ...quote.tradedMarket, isBestBid, isBestOffer },
      offer: { ...quote.tradedMarket, isBestBid, isBestOffer },
    };
  }

  // Else down here there is no traded market. We are either rejected (terminal), pending new, or open...
  // We just elevate whichever is the best bid/offers
  const openBests = calculateBestQuotes({
    quotes: quote.Markets,
    pricingMode: quote.PricingMode,
    onlyActive: true, // true = only calculate based on Open market quotes
    bestBy,
  });
  const bestBid = openBests.find(m => m.isBestBid);
  const bestOffer = openBests.find(m => m.isBestOffer);

  // If there is no bestBid or bestOffer yet, we are OK with returning undefined here. Renders as empty cells.
  return { bid: bestBid, offer: bestOffer };
}
